import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Gift, Copy, Users, MessageCircle } from "lucide-react";
import T from "@/components/T";

export default function ReferralView({ ch, setCh }) {
  const [data, setData] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => setData(await api.referralsMe());
  useEffect(() => { refresh(); }, []);

  const shareLink = data
    ? `${window.location.origin}/login?ref=${data.code}`
    : "";

  const copy = () => {
    navigator.clipboard.writeText(shareLink);
    toast.success("Referral link copied");
  };

  const apply = async () => {
    if (!code.trim() || busy) return;
    setBusy(true);
    try {
      const updated = await api.referralsApply(code.trim());
      setCh(updated);
      setCode("");
      await refresh();
      toast.success("Referral applied. Blood-Moon Cloak granted!");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="panel p-4 text-center text-[#666] text-sm italic">Fetching your seal...</div>;

  return (
    <div className="space-y-2">
      <div className="panel p-3 space-y-2" data-testid="referral-my-code">
        <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c] flex items-center gap-2">
          <Gift className="w-4 h-4 text-[#E5C34A]" strokeWidth={1.5} /> <T>Your Referral</T>
        </div>
        <p className="italic text-[10px] text-[#666]">
          <T>Share your code. When a friend claims it, both of you receive the exclusive Blood-Moon Cloak.</T>
        </p>
        <div className="flex items-center gap-2">
          <div
            className="flex-1 bg-[#0a0a0a] border border-[#262626] px-3 py-2 font-mono text-lg text-[#E5C34A] tracking-widest text-center"
            data-testid="referral-code"
          >
            {data.code}
          </div>
          <button
            data-testid="referral-copy"
            onClick={copy}
            className="px-3 py-2 border border-[#B89C3A] text-[#E5C34A] font-heading text-[10px] tracking-widest uppercase flex items-center gap-1"
          >
            <Copy className="w-3 h-3" strokeWidth={1.5} /> Copy Link
          </button>
        </div>
        <div className="font-mono text-[10px] text-[#666] break-all">{shareLink}</div>
      </div>

      {!data.referred_by && (
        <div className="panel p-3 space-y-2" data-testid="referral-apply">
          <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c]">
            <T>Redeem a Code</T>
          </div>
          <div className="flex gap-2">
            <input
              data-testid="referral-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="6-char code"
              maxLength={6}
              className="flex-1 bg-[#0a0a0a] border border-[#262626] px-3 py-2 text-sm text-[#E2DFD8] tracking-widest text-center font-mono"
            />
            <button
              data-testid="referral-apply-btn"
              onClick={apply}
              disabled={busy || code.length !== 6}
              className="px-3 py-2 border border-[#4A5D23] text-[#6E8B3D] font-heading text-[10px] tracking-widest uppercase disabled:opacity-30"
            >
              Redeem
            </button>
          </div>
        </div>
      )}

      <div className="panel p-3" data-testid="referral-list">
        <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c] mb-2 flex items-center gap-2">
          <Users className="w-3 h-3" strokeWidth={1.5} /> Souls Recruited ({data.referred_count})
        </div>
        {(data.referrals || []).length === 0 && (
          <p className="italic text-[#666] text-xs">Nobody yet. The road is long.</p>
        )}
        {(data.referrals || []).map((r, i) => (
          <div key={i} className="flex justify-between text-xs font-mono border-b border-[#1a1a1a] py-1">
            <span className="text-[#E2DFD8]">{r.referee_name}</span>
            <span className="text-[#666]">{new Date(r.at).toLocaleDateString()}</span>
          </div>
        ))}
      </div>

      
       

    </div>
  );
}
